/**
 * Edit › Reels — the reels a contest owes and the Reel Options on them
 * (CAE-1552).
 *
 * Spec: [`06-edit-reels/README.md`](../../../../../../docs/design/admin-ui/06-edit-reels/README.md);
 * values from `.rl-*` in
 * [`prototype/modals.css`](../../../../../../docs/design/admin-ui/prototype/modals.css).
 * Where the two disagree the stylesheet wins, and the places they do are listed
 * in `prototype/README.md`.
 *
 * This replaced `contest-reel-options-panel.tsx`, a 1,442-line component that was
 * the largest in the admin. The rebuild is a restructure, not a rewrite: every
 * mutation it carried is the same request behind the same confirm, lifted into
 * `reels/use-reel-option-mutations.ts` where the screen's composition can reach
 * it without 300 lines of asset-picker markup in between.
 *
 * **A slot per reel the contest owes, always** — see `reels/reel-slots.ts` for
 * why, and why the handoff's full-page "No reels yet" state is not among the
 * states this screen has.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { assetsApi } from '../../../api/assets';
import { resolvePickName } from '@s2w/core';
import {
  defaultReelKind,
  type ContestDetailReelOption,
  type ReelKind,
} from '../../../api/contests';
import { RemoveConfirmationDialog } from '../../../components/ui';
import { contestFixturesById } from '../detail-tabs/contest-detail-tab-utils';
import { DisplayNameOverrideDialog } from '../display-name-override-dialog';
import { contestIsEditable } from '../status-transitions';
import { useContestEdit } from './contest-edit-context';
import { frameBleedSx } from '../../../components/layout';
import { AddOutcomesModal } from './reels/add-outcomes-modal';
import { AssetPickerDialog } from '../../../components/asset-picker-dialog';
import { ReelOptionParticipantDialog } from './reels/reel-option-participant-dialog';
import { ReelDetail } from './reels/reel-detail';
import { ReelSelectorRail } from './reels/reel-selector-rail';
import { toReelSlots } from './reels/reel-slots';
import { useReelOptionMutations } from './reels/use-reel-option-mutations';

const RAIL_TAB_LIST_ID = 'reel-rail';
const DETAIL_PANEL_ID = 'reel-detail';

export interface ReelsStepSearch {
  /** The Reel to open on. Absent is Reel 1 — the rail's own default. */
  reel?: number;
}

/**
 * `?reel=N` — the Reel this screen opens on (CAE-1595).
 *
 * The publish readiness checklist names each enabled Reel Option with no image
 * and links each one to its Reel; the rail's selection was component state with
 * no way in from a URL, so there was nothing for those links to say.
 *
 * Anything that is not a positive integer is dropped rather than refused: a
 * stale or hand-edited link should open the screen on Reel 1, which is where it
 * opened before this param existed, and not throw the operator out of the Edit
 * flow over a query string.
 */
export const validateReelsSearch = (search: Record<string, unknown>): ReelsStepSearch => {
  const reel = Number(search.reel);
  return Number.isInteger(reel) && reel > 0 ? { reel } : {};
};

export const ContestEditReelsStep = () => {
  const edit = useContestEdit();
  /*
   * Seeded from the URL, then owned by the rail.
   *
   * `strict: false` because this component is mounted by the file route in the
   * app and by a hand-built route in its tests, and the question it is asking —
   * "was a Reel named on the way in?" — is the same under both.
   */
  const search = useSearch({ strict: false }) as ReelsStepSearch;
  const [selectedReelNumber, setSelectedReelNumber] = useState(search.reel ?? 1);

  /*
   * Follow the URL when it moves on its own.
   *
   * Seeding `useState` is not enough: TanStack does not remount a route
   * component when only the search changes, so Back and Forward between
   * `?reel=4` and `?reel=2` moved the URL and left the rail where it was —
   * the screen then disagreeing with its own address bar. Reachable the
   * moment an operator follows two of the checklist's named options and
   * presses Back.
   *
   * One direction only. The rail is a control, not a destination (CAE-1552
   * gave it no URL), so selecting a Reel still does not push history; this
   * only takes a Reel the URL names.
   */
  useEffect(() => {
    if (search.reel !== undefined) setSelectedReelNumber(search.reel);
  }, [search.reel]);
  const [addOutcomesOpen, setAddOutcomesOpen] = useState(false);
  /** The kind the Operator picked for each Reel not yet created, by Reel number. */
  const [chosenKinds, setChosenKinds] = useState<Record<number, ReelKind>>({});
  const [pendingRemoval, setPendingRemoval] = useState<ContestDetailReelOption | null>(null);
  /**
   * The Reel Option whose image slot was clicked, with the reel it was on.
   *
   * The reel is remembered rather than read back off the rail: the operator can
   * select another reel while the picker is open, and attaching to the row they
   * clicked is right where attaching to whatever now sits in its place is not.
   * The panel this replaced closed the dialog and said so instead — kept below.
   */
  const [assetTarget, setAssetTarget] = useState<{
    reelNumber: number;
    option: ContestDetailReelOption;
  } | null>(null);
  const [renameTarget, setRenameTarget] = useState<ContestDetailReelOption | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  /** The Reel Option whose club and number are being set (CAE-1610). */
  const [participantTarget, setParticipantTarget] = useState<ContestDetailReelOption | null>(null);

  const mutations = useReelOptionMutations({
    contestId: edit.contestId,
    contestDetailQueryKey: edit.detailQueryKey,
    onNotice: edit.setNotice,
  });

  /*
   * The artwork behind every `assetId` on every reel, in one request. Carried
   * over from the panel this replaced, cache key and all — the alternative is a
   * request per row, and a reel of eight options is eight of them on tab open.
   */
  const assetsQuery = useQuery({
    queryKey: ['admin', 'assets', 'all-for-reels'],
    queryFn: () => assetsApi.list({}),
  });
  const assetsById = useMemo(
    () => new Map((assetsQuery.data ?? []).map((asset) => [asset.id, asset])),
    [assetsQuery.data],
  );

  const fixturesById = useMemo(
    () => contestFixturesById(edit.detail?.fixtures ?? []),
    [edit.detail],
  );

  const slots = toReelSlots(edit.detail?.reels ?? [], edit.detail?.contest ?? {});
  // Falls back rather than rendering nothing: `selectedReelNumber` is seeded
  // before the contest loads, and a payload carrying a reel beyond the contest's
  // own reel count can still be navigated away from.
  const slot = slots.find((candidate) => candidate.reelNumber === selectedReelNumber) ?? slots[0];

  /*
   * Edits go when editing does — the same window the header's Save sits behind.
   * Unlike the Fixtures step, *every* control here writes, so the gate is on the
   * whole panel rather than on one affordance.
   */
  const status = edit.detail?.contest.status;
  const editable = status != null && contestIsEditable(status);

  const busy =
    mutations.setEnabled.isPending || mutations.reorder.isPending || mutations.remove.isPending;

  if (!slot) return null;

  const newReelKind =
    chosenKinds[slot.reelNumber] ?? defaultReelKind(slot.reelNumber, edit.detail?.contest.sport);

  return (
    <Box
      /*
       * `.rl-frame` zeroes the frame card's own 28px padding. The rail brings its
       * own and carries the divider between the two columns, which has to run the
       * full height of the card — it cannot do that inside a padded box. Same
       * device as `.bi-frame` and `.fx-frame` on the two steps before this;
       * `<FrameCard>` belongs to the layout and is shared with all four.
       */
      sx={(theme: Theme) => ({
        ...frameBleedSx(theme),
        display: 'grid',
        // `minmax(0, 1fr)` rather than the stylesheet's `1fr`: an `auto` minimum
        // would let the outcome table's widest row set the column's floor and
        // push the page into a horizontal scrollbar, which the table handles
        // inside itself instead.
        gridTemplateColumns: '280px minmax(0, 1fr)',
        [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' },
      })}
    >
      <ReelSelectorRail
        slots={slots}
        selected={slot.reelNumber}
        onSelect={setSelectedReelNumber}
        panelId={DETAIL_PANEL_ID}
        tabListId={RAIL_TAB_LIST_ID}
      />

      <ReelDetail
        slot={slot}
        id={DETAIL_PANEL_ID}
        labelledBy={`${RAIL_TAB_LIST_ID}-${slot.reelNumber}`}
        fixturesById={fixturesById}
        assetsById={assetsById}
        editable={editable}
        busy={busy}
        newReelKind={newReelKind}
        onChooseKind={(kind) =>
          setChosenKinds((previous) => ({ ...previous, [slot.reelNumber]: kind }))
        }
        onAddOutcomes={() => setAddOutcomesOpen(true)}
        onRename={(label) =>
          mutations.renameReel.mutate({
            reelNumber: slot.reelNumber,
            label,
            reel: slot.reel,
            kind: newReelKind,
          })
        }
        onToggleEnabled={(option, enabled) =>
          mutations.setEnabled.mutate({
            reelNumber: slot.reelNumber,
            optionId: option.id,
            enabled,
          })
        }
        onMoveTo={(option, position) =>
          mutations.reorder.mutate({
            reelNumber: slot.reelNumber,
            optionId: option.id,
            position,
          })
        }
        onPickAsset={(option) => setAssetTarget({ reelNumber: slot.reelNumber, option })}
        onRemove={setPendingRemoval}
        onEditParticipant={setParticipantTarget}
        onEditDisplayName={(option) => {
          setRenameDraft(option.displayNameOverride ?? '');
          setRenameTarget(option);
        }}
        onClearAsset={(option) =>
          mutations.setAsset.mutate({
            reelNumber: slot.reelNumber,
            optionId: option.id,
            assetId: null,
          })
        }
      />

      <DisplayNameOverrideDialog
        open={renameTarget !== null}
        title="Edit reel option"
        canonicalName={renameTarget?.outcomeName}
        value={renameDraft}
        onChange={setRenameDraft}
        saving={mutations.setDisplayNameOverride.isPending}
        saveDisabled={renameTarget === null}
        helperText="Shown on the spinner and on pick cards. Leave empty to use the catalogue name."
        onClose={() => setRenameTarget(null)}
        onSave={() => {
          if (!renameTarget) return;
          mutations.setDisplayNameOverride.mutate({
            reelNumber: slot.reelNumber,
            optionId: renameTarget.id,
            displayNameOverride: renameDraft,
          });
          setRenameTarget(null);
        }}
      />

      <ReelOptionParticipantDialog
        open={participantTarget !== null}
        option={participantTarget}
        sportId={edit.detail?.contest.sportId}
        saving={mutations.setParticipant.isPending}
        onClose={() => setParticipantTarget(null)}
        onSave={(participant) => {
          if (!participantTarget) return;
          mutations.setParticipant.mutate({
            reelNumber: slot.reelNumber,
            optionId: participantTarget.id,
            participant,
          });
          setParticipantTarget(null);
        }}
      />

      <AssetPickerDialog
        open={assetTarget !== null}
        subject={assetTarget ? resolvePickName(assetTarget.option) : ''}
        isAttaching={mutations.setAsset.isPending}
        onNotice={edit.setNotice}
        onClose={() => setAssetTarget(null)}
        onAttach={(assetId) => {
          if (!assetTarget) return;
          if (assetTarget.reelNumber !== slot.reelNumber) {
            setAssetTarget(null);
            edit.setNotice('Asset selector was closed after changing reel context.');
            return;
          }
          mutations.setAsset.mutate(
            { reelNumber: assetTarget.reelNumber, optionId: assetTarget.option.id, assetId },
            { onSuccess: () => setAssetTarget(null) },
          );
        }}
      />

      <AddOutcomesModal
        open={addOutcomesOpen}
        reelLabel={slot.title}
        fixtures={edit.detail?.fixtures ?? []}
        reelOutcomeIds={(slot.reel?.options ?? []).map((option) => option.externalOutcomeId)}
        isPending={mutations.addOutcomes.isPending}
        onClose={() => setAddOutcomesOpen(false)}
        onConfirm={(picks) =>
          mutations.addOutcomes.mutate(
            /*
             * The slot's Reel, which is null until one is configured. The
             * mutation needs it to decide whether its upsert is creating the
             * Reel — and so whether to declare a kind — not just renaming it.
             */
            { reelNumber: slot.reelNumber, picks, reel: slot.reel ?? null, kind: newReelKind },
            {
              /*
               * Closed only when the whole batch landed. `addOutcomes` runs to
               * the end and never rejects, so closing on every settle would
               * throw away a selection the operator can still retry from — the
               * ones that did land have left it already, because the modal drops
               * any pick the reel now holds.
               */
              onSuccess: ({ failed }) => {
                if (failed.length === 0) setAddOutcomesOpen(false);
              },
            },
          )
        }
      />

      <RemoveConfirmationDialog
        open={pendingRemoval !== null}
        title="Remove reel option?"
        isPending={mutations.remove.isPending}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (!pendingRemoval) return;
          mutations.remove.mutate(
            { reelNumber: slot.reelNumber, optionId: pendingRemoval.id },
            { onSuccess: () => setPendingRemoval(null) },
          );
        }}
        confirmLabel="Remove option"
        cancelTestId="confirm-remove-reel-option-dialog-cancel"
        confirmTestId="confirm-remove-reel-option-dialog-confirm"
      >
        {pendingRemoval ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box component="p" sx={{ margin: 0 }}>
              This permanently removes the outcome from {slot.title}. You can add it again from the
              catalogue if needed.
            </Box>
            <Box component="p" sx={{ margin: 0, fontWeight: 700 }}>
              {/*
               * `resolvePickName`, not `||` — on the one dialog whose whole job
               * is to say which option is about to be deleted. An override of
               * `'   '` passes `||` and leaves this line blank; the column is
               * unconstrained, which is exactly why CONTEXT.md puts the rule in
               * `@s2w/core`. The row behind this dialog already resolves it, so
               * the two would otherwise disagree about the same option.
               */}
              {resolvePickName(pendingRemoval)}
            </Box>
          </Box>
        ) : null}
      </RemoveConfirmationDialog>
    </Box>
  );
};
